import React, { useCallback, useEffect, useRef, useState } from "react";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import Stack from "@mui/material/Stack";
import Popper from "@mui/material/Popper";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useLocation, useNavigate } from "react-router-dom";
import SubscriptionService from "../../../services/SubscriptionService";
import UserService from "../../../services/UserService";
import { SUBSCRIPTION_PLANS } from "../../subscription/PanelPlan";
import { usePagesTranslation } from "../../locales";
import WordWheelService from "./WordWheelService";
import { loadGuestPuzzleCoins } from "./guestCoinsStorage";
import { ACCENT, SURFACE } from "../../../dark-theme/palette";
import {
    ShopHeroIcon,
    closeCtaSx,
    goldCtaSx,
    goldShopPaperSx,
    greenCtaSx,
    tealShopPaperSx,
} from "./shopUi";

export const WW_WALLET_COINS_EVENT = "ww-wallet-coins";
export const WW_DAILY_GIFT_EVENT = "ww-daily-gift";

let lastDailyGift = { pending: false, grant: 0 };

export function getWordWheelDailyGiftState() {
    return lastDailyGift;
}

export function notifyWordWheelWalletCoins(coins) {
    if (typeof window === "undefined") return;
    window.dispatchEvent(new CustomEvent(WW_WALLET_COINS_EVENT, { detail: { coins } }));
}

export function notifyWordWheelDailyGift(pending, grant = 0) {
    lastDailyGift = {
        pending: Boolean(pending),
        grant: Math.max(0, Number(grant) || 0),
    };
    if (typeof window === "undefined") return;
    window.dispatchEvent(new CustomEvent(WW_DAILY_GIFT_EVENT, { detail: lastDailyGift }));
}

/** Claim today's visit gift. No-op if already claimed. Does not grant unless this call succeeds. */
export async function collectWordWheelDailyGift() {
    const gift = await WordWheelService.claimDailyGift();
    if (gift?.wordWheelCoins != null) {
        notifyWordWheelWalletCoins(Number(gift.wordWheelCoins) || 0);
    }
    notifyWordWheelDailyGift(false, 0);
    return gift;
}

export const WORD_WHEEL_STARTER_PLAN = SUBSCRIPTION_PLANS.starter_fun_bundle;
export const WORD_WHEEL_CLASSIC_PLAN = SUBSCRIPTION_PLANS.classic_challenge;
export const WORD_WHEEL_COINS_PLAN = SUBSCRIPTION_PLANS.pack300;
export const WORD_WHEEL_CREDIT_PLANS = [WORD_WHEEL_STARTER_PLAN, WORD_WHEEL_CLASSIC_PLAN];

export function isWordWheelCreditPlan(plan) {
    return plan?.priceKey === "starter_fun_bundle" || plan?.priceKey === "classic_challenge";
}

function planCreditCount(plan) {
    if (plan?.priceKey === "classic_challenge") return 50;
    return 150;
}

function WalletChip({ icon, label, value, onClick, ariaLabel, tone = "play", showLabel = false, buttonRef = null }) {
    const dashboard = tone === "dashboard";
    const button = (
        <Button
            ref={buttonRef}
            onClick={onClick}
            aria-label={ariaLabel || label}
            sx={
                dashboard
                    ? {
                        minHeight: 44,
                        px: 1.5,
                        borderRadius: 5,
                        textTransform: "none",
                        color: "text.primary",
                        bgcolor: SURFACE.card,
                        border: "1px solid",
                        borderColor: SURFACE.border,
                        overflow: "visible",
                        "&:hover": { borderColor: ACCENT.main, bgcolor: SURFACE.sunken || SURFACE.card },
                    }
                    : {
                        minHeight: 44,
                        minWidth: 44,
                        px: 1.25,
                        borderRadius: 5,
                        textTransform: "none",
                        color: "#ecfdf5",
                        bgcolor: "rgba(8, 47, 73, 0.55)",
                        border: "1px solid rgba(125, 211, 252, 0.45)",
                        "&:hover": { bgcolor: "rgba(8, 47, 73, 0.72)" },
                    }
            }
        >
            {icon}
            {showLabel ? (
                <Typography component="span" sx={{ ml: 0.75, fontWeight: 700, fontSize: "0.82rem", opacity: 0.82 }}>
                    {label}
                </Typography>
            ) : null}
            <Typography component="span" sx={{ ml: 0.75, fontWeight: 800, fontSize: "0.95rem" }}>
                {value}
            </Typography>
        </Button>
    );
    if (showLabel) return button;
    return <Tooltip title={label}>{button}</Tooltip>;
}

export default function WordWheelWallet({
    compact = false,
    onNeedPurchase,
    openShopKind = null,
    onOpenShopKindConsumed,
    tone = "play",
    hideGift: _hideGift = false,
    showLabels = false,
}) {
    const { t } = usePagesTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [credits, setCredits] = useState(0);
    const [coins, setCoins] = useState(0);
    const [giftNote, setGiftNote] = useState("");
    const [giftPending, setGiftPending] = useState(false);
    const [giftGrant, setGiftGrant] = useState(0);
    const [giftMessageOpen, setGiftMessageOpen] = useState(false);
    const coinsBtnRef = useRef(null);
    const [shop, setShop] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [signedIn, setSignedIn] = useState(false);

    const refresh = useCallback(async () => {
        const loggedIn = await WordWheelService.isLoggedIn();
        setSignedIn(loggedIn);
        if (!loggedIn) {
            setCredits(0);
            setCoins(loadGuestPuzzleCoins());
            return;
        }
        try {
            const [balance, user] = await Promise.all([
                WordWheelService.getCreditBalance(),
                UserService.getUserInfo().catch(() => null),
            ]);
            setCredits(Number(balance?.creditBalance) || 0);
            const wwCoins = user?.cloudUser?.puzzleCoins?.word_wheel_quest;
            setCoins(Number(wwCoins) || 0);
        } catch {
            /* keep last */
        }
    }, []);

    useEffect(() => {
        const onCoins = (event) => {
            const n = Number(event?.detail?.coins);
            if (Number.isFinite(n) && n >= 0) setCoins(Math.floor(n));
        };
        const onGift = (event) => {
            setGiftPending(Boolean(event?.detail?.pending));
            const grant = Number(event?.detail?.grant);
            if (Number.isFinite(grant) && grant >= 0) setGiftGrant(grant);
        };
        window.addEventListener(WW_WALLET_COINS_EVENT, onCoins);
        window.addEventListener(WW_DAILY_GIFT_EVENT, onGift);
        return () => {
            window.removeEventListener(WW_WALLET_COINS_EVENT, onCoins);
            window.removeEventListener(WW_DAILY_GIFT_EVENT, onGift);
        };
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            await refresh();
            const loggedIn = await WordWheelService.isLoggedIn();
            if (!loggedIn || cancelled) return;
            try {
                const peek = await WordWheelService.peekDailyGift();
                if (cancelled) return;
                const available = Boolean(peek?.available);
                const grant = Number(peek?.grant) || 0;
                setGiftPending(available);
                setGiftGrant(available ? grant : 0);
                notifyWordWheelDailyGift(available, available ? grant : 0);
            } catch {
                /* ignore */
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [refresh]);

    const goLogin = () => {
        navigate("/login", {
            state: { from: { pathname: `${location.pathname}${location.search || ""}` } },
        });
    };

    const openShop = (kind, plan) => {
        if (onNeedPurchase) onNeedPurchase(plan || (kind === "coins" ? WORD_WHEEL_COINS_PLAN : WORD_WHEEL_STARTER_PLAN));
        setError("");
        setShop(kind);
    };

    const closeShop = () => {
        setShop(null);
        setError("");
    };

    useEffect(() => {
        if (!openShopKind) return;
        openShop(openShopKind);
        onOpenShopKindConsumed?.();
    }, [openShopKind, onOpenShopKindConsumed]);

    const startCheckout = async (plan) => {
        if (!plan) return;
        if (!signedIn) {
            goLogin();
            return;
        }
        setBusy(true);
        setError("");
        try {
            const userData = await UserService.getUserInfo();
            const email = userData?.user?.email || userData?.cloudUser?.email;
            if (!email) {
                setError(t("wordwheel.checkout-failed"));
                return;
            }
            const result = await SubscriptionService.createCheckoutSession(plan, email);
            const url = result?.data?.url || result?.data?.checkoutUrl;
            if (!result.success || !url) {
                setError(result.error || t("wordwheel.checkout-failed"));
                return;
            }
            window.location.assign(url);
        } catch (e) {
            setError(e?.message || t("wordwheel.checkout-failed"));
        } finally {
            setBusy(false);
        }
    };

    const shopOpen = Boolean(shop);

    useEffect(() => {
        if (!giftMessageOpen) return undefined;
        const timer = window.setTimeout(() => setGiftMessageOpen(false), 5000);
        return () => window.clearTimeout(timer);
    }, [giftMessageOpen]);

    const handleCoinsClick = async () => {
        if (giftPending) {
            try {
                const gift = await collectWordWheelDailyGift();
                const granted = Number(gift?.granted) || giftGrant || 1;
                if (granted > 0) {
                    setGiftNote(t("wordwheel.gift-granted", { count: granted }));
                    setGiftMessageOpen(true);
                }
                setGiftPending(false);
                setGiftGrant(0);
            } catch {
                /* keep pending so they can retry */
            }
            return;
        }
        openShop("coins", WORD_WHEEL_COINS_PLAN);
    };

    const coinsBulbIcon = (
        <Box sx={{ position: "relative", display: "inline-flex", overflow: "visible" }}>
            <LightbulbOutlinedIcon sx={{ fontSize: 20 }} />
            {giftPending ? (
                <CardGiftcardIcon
                    sx={{
                        position: "absolute",
                        top: -7,
                        right: -8,
                        fontSize: 15,
                        color: "#facc15",
                        filter: "drop-shadow(0 1px 3px rgba(0,0,0,0.45))",
                        animation: "wwGiftArrive 1.15s ease-in-out infinite",
                        "@keyframes wwGiftArrive": {
                            "0%, 100%": { transform: "translateY(0) scale(1)" },
                            "50%": { transform: "translateY(-2px) scale(1.12)" },
                        },
                    }}
                />
            ) : null}
        </Box>
    );

    return (
        <>
            <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent={tone === "dashboard" || compact ? "flex-end" : "center"}
                sx={{ flexWrap: "wrap", rowGap: 1, overflow: "visible" }}
            >
                {tone === "dashboard" ? (
                    <WalletChip
                        tone={tone}
                        showLabel={showLabels}
                        icon={coinsBulbIcon}
                        label={t("dashboard.coins")}
                        value={coins}
                        buttonRef={coinsBtnRef}
                        onClick={handleCoinsClick}
                        ariaLabel={giftPending ? t("wordwheel.gift-tap", "Daily visit gift — tap to collect") : t("wordwheel.buy-coins")}
                    />
                ) : null}
                <WalletChip
                    tone={tone}
                    showLabel={showLabels}
                    icon={<VisibilityOutlinedIcon sx={{ fontSize: 20 }} />}
                    label={tone === "dashboard" ? t("dashboard.credits") : t("wordwheel.credits-label")}
                    value={credits}
                    onClick={() => openShop("credits")}
                    ariaLabel={t("wordwheel.buy-credits")}
                />
                {compact || tone === "dashboard" ? null : (
                    <WalletChip
                        tone={tone}
                        showLabel={showLabels}
                        icon={coinsBulbIcon}
                        label={t("wordwheel.coins-label")}
                        value={coins}
                        buttonRef={coinsBtnRef}
                        onClick={handleCoinsClick}
                        ariaLabel={giftPending ? t("wordwheel.gift-tap", "Daily visit gift — tap to collect") : t("wordwheel.buy-coins")}
                    />
                )}
            </Stack>

            <Dialog
                open={shopOpen}
                onClose={closeShop}
                slotProps={{
                    paper: {
                        sx: shop === "coins" ? goldShopPaperSx : tealShopPaperSx,
                    },
                }}
            >
                {shop === "credits" ? (
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}>
                        <ShopHeroIcon tone="teal">
                            <VisibilityOutlinedIcon sx={{ fontSize: 44, color: "#0284c7" }} />
                        </ShopHeroIcon>
                        <Typography sx={{ fontWeight: 800, fontSize: "1.2rem", lineHeight: 1.3, mb: 0.5 }}>
                            {t("wordwheel.buy-credits")}
                        </Typography>
                        <Typography sx={{ fontWeight: 600, mb: 1.75, opacity: 0.85 }}>
                            {t("wordwheel.credit-reveal")}
                        </Typography>
                        <Stack spacing={1.5} sx={{ width: "100%" }}>
                            {WORD_WHEEL_CREDIT_PLANS.map((plan) => {
                                const popular = Boolean(plan.popular);
                                const PlanIcon = plan.priceKey === "classic_challenge" ? WorkspacePremiumIcon : StarRoundedIcon;
                                return (
                                    <Box
                                        key={plan.priceKey}
                                        sx={{
                                            position: "relative",
                                            border: popular
                                                ? "2px solid #22c55e"
                                                : "1px solid rgba(14, 116, 144, 0.28)",
                                            borderRadius: 2.5,
                                            p: 1.75,
                                            pt: popular ? 2.25 : 1.75,
                                            bgcolor: popular ? "rgba(240, 253, 244, 0.92)" : "rgba(255,255,255,0.82)",
                                            textAlign: "left",
                                            boxShadow: popular
                                                ? "0 10px 22px rgba(22, 163, 74, 0.16)"
                                                : "0 6px 16px rgba(14, 116, 144, 0.08)",
                                            transition: "transform 0.18s ease, box-shadow 0.18s ease",
                                            "&:hover": {
                                                transform: "translateY(-2px)",
                                                boxShadow: popular
                                                    ? "0 14px 28px rgba(22, 163, 74, 0.22)"
                                                    : "0 10px 22px rgba(14, 116, 144, 0.14)",
                                            },
                                        }}
                                    >
                                        {popular ? (
                                            <Box
                                                sx={{
                                                    position: "absolute",
                                                    top: -11,
                                                    left: 14,
                                                    px: 1.1,
                                                    py: 0.2,
                                                    borderRadius: 999,
                                                    bgcolor: "#16a34a",
                                                    color: "#fff",
                                                    fontSize: "0.7rem",
                                                    fontWeight: 800,
                                                    letterSpacing: "0.04em",
                                                    textTransform: "uppercase",
                                                }}
                                            >
                                                {t("wordwheel.popular-plan")}
                                            </Box>
                                        ) : null}
                                        <Stack direction="row" spacing={1.25} alignItems="flex-start">
                                            <Box
                                                sx={{
                                                    width: 44,
                                                    height: 44,
                                                    borderRadius: 2,
                                                    flexShrink: 0,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    bgcolor: popular ? "#dcfce7" : "#e0f2fe",
                                                    color: popular ? "#15803d" : "#0369a1",
                                                }}
                                            >
                                                <PlanIcon />
                                            </Box>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                                                    {plan.name}
                                                </Typography>
                                                <Typography variant="body2" sx={{ mt: 0.35, fontWeight: 700, color: "#0e7490" }}>
                                                    {t("wordwheel.credits-count", { count: planCreditCount(plan) })}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 1.25 }}>
                                            <Typography sx={{ fontWeight: 800, fontSize: "1.25rem" }}>{plan.price}</Typography>
                                            <Button
                                                variant="contained"
                                                onClick={() => startCheckout(plan)}
                                                disabled={busy}
                                                startIcon={<PlanIcon />}
                                                sx={greenCtaSx}
                                            >
                                                {t("wordwheel.buy")}
                                            </Button>
                                        </Stack>
                                    </Box>
                                );
                            })}
                        </Stack>
                        {error ? <Alert severity="error" sx={{ mt: 2, width: "100%" }}>{error}</Alert> : null}
                        <Button onClick={closeShop} sx={{ ...closeCtaSx, mt: 1.5 }}>
                            {t("wordwheel.close")}
                        </Button>
                    </Box>
                ) : (
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}>
                        <ShopHeroIcon>
                            <MonetizationOnIcon sx={{ fontSize: 52, color: "#d97706" }} />
                        </ShopHeroIcon>
                        <Typography sx={{ fontWeight: 800, fontSize: "1.2rem", lineHeight: 1.3, mb: 0.75 }}>
                            {WORD_WHEEL_COINS_PLAN.name}
                        </Typography>
                        <Typography sx={{ fontWeight: 600, mb: 1.25, lineHeight: 1.4 }}>
                            {t("wordwheel.coins-copy")}
                        </Typography>
                        <Typography sx={{ fontWeight: 800, fontSize: "1.35rem", mb: 2 }}>
                            {WORD_WHEEL_COINS_PLAN.price}
                        </Typography>
                        {error ? <Alert severity="error" sx={{ mb: 1.5, width: "100%" }}>{error}</Alert> : null}
                        <Stack direction="row" spacing={1} sx={{ width: "100%" }}>
                            <Button
                                onClick={closeShop}
                                sx={{
                                    ...closeCtaSx,
                                    flex: 1,
                                    color: "#b45309",
                                    border: "1.5px solid rgba(217, 119, 6, 0.45)",
                                    bgcolor: "rgba(255, 255, 255, 0.7)",
                                    "&:hover": { bgcolor: "#fff", borderColor: "#d97706" },
                                }}
                            >
                                {t("wordwheel.close")}
                            </Button>
                            <Button
                                variant="contained"
                                onClick={() => startCheckout(WORD_WHEEL_COINS_PLAN)}
                                disabled={busy}
                                startIcon={<MonetizationOnIcon />}
                                sx={{ ...goldCtaSx, flex: 1.3 }}
                            >
                                {t("wordwheel.buy")}
                            </Button>
                        </Stack>
                    </Box>
                )}
            </Dialog>
            <Popper
                open={giftMessageOpen}
                anchorEl={coinsBtnRef.current}
                placement="top"
                modifiers={[{ name: "offset", options: { offset: [0, 10] } }]}
                sx={{ zIndex: (theme) => theme.zIndex.snackbar }}
            >
                <Box
                    role="status"
                    sx={{
                        px: 1.5,
                        py: 0.85,
                        borderRadius: 2,
                        bgcolor: "rgba(8, 47, 73, 0.96)",
                        color: "#ecfdf5",
                        border: "1px solid rgba(125, 211, 252, 0.7)",
                        boxShadow: "0 10px 24px rgba(0, 0, 0, 0.4)",
                        fontWeight: 700,
                        fontSize: "0.9rem",
                        lineHeight: 1.35,
                        maxWidth: 260,
                        textAlign: "center",
                    }}
                >
                    {giftNote || t("wordwheel.gift-granted", { count: 1 })}
                </Box>
            </Popper>
        </>
    );
}

export { WalletChip };
